import { Link } from '@mui/material';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  CheckCircleIcon,
  WarningCircleIcon,
  WarningIcon,
  WarningOctagonIcon,
} from '@phosphor-icons/react';
import React from 'react';

import { parseError } from './errors.js';
import { isDefined } from '../../utils/types.js';
import { mergeSxProps } from '../Mui/mergeSxProps.js';
import { Text } from '../Typography/index.js';

export type AlertVariant = 'info' | 'warning' | 'error' | 'success';

export interface AlertCta {
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * The label for the CTA.
   */
  label: string;

  /**
   * The action to take when clicking on the CTA.
   */
  onClick: React.MouseEventHandler;
}

export interface AlertProps {
  /**
   * The content of the component.
   */
  children?: React.ReactNode;

  /**
   * Call to action object props.
   */
  cta?: AlertCta;

  /**
   * An error to display as the main text.
   * Passing an error changes the default variant to `error`.
   * @type Error | unknown
   */
  error?: unknown;

  /**
   * Hides the alert icon, if true.
   * @default false
   */
  hideIcon?: boolean;

  /**
   * Plain alerts don't have an outline or background.
   * Should not be used with a title or CTA.
   * @default false
   */
  plain?: boolean;

  /**
   * The title text to display in the Alert.
   */
  title?: string;

  /**
   * The alert variant to use.
   * @default "info", or "error" if an error is passed
   */
  variant?: AlertVariant;

  sx?: SxProps<Theme>;
}

const AlertPropsByVariant: Record<
  AlertVariant,
  { backgroundColor: string; borderColor: string; iconColor: string; Icon: React.ElementType }
> = {
  info: {
    backgroundColor: '#f7f7f7',
    borderColor: '#bfbfbf',
    iconColor: '#595959',
    Icon: WarningCircleIcon,
  },
  warning: {
    backgroundColor: '#fff7e5',
    borderColor: '#e59d00',
    iconColor: '#ffae00',
    Icon: WarningIcon,
  },
  error: {
    backgroundColor: '#fce8e8',
    borderColor: '#b81414',
    iconColor: '#eb4747',
    Icon: WarningOctagonIcon,
  },
  success: {
    backgroundColor: '#ebf9f1',
    borderColor: '#2e9e5d',
    iconColor: '#2e9e5d',
    Icon: CheckCircleIcon,
  },
};

/**
 * We use alerts within all of our apps, and have some slightly different methods of applying them to the product.
 * However, the styling of all alerts follow the same formats regardless of which app they are being used in.
 *
 * Informational alerts are used to display supplementary information to help support a header with additional context,
 * or give additional information on the expected behavior of a feature.
 * Treat these as "low priority", and use them when you don't need to go into as much depth as a contextual helper.
 * These alerts always fill the full container space available.
 * The icon should be aligned with the top line of text, unless the text only spans 1 line.
 * When the text only spans 1 line, it should be aligned vertically with the icon.
 * Info alerts use the same icon for every instance.
 *
 * These medium priority alerts are used when there is a need to increase visual attention to the alert.
 * They will typically be associated with components that may have an impact on the user.
 * For example: Saving an update on an item that will impact other items down the chain.
 *
 * Error alerts are the highest priority alerts, and are used sparingly.
 * These alerts are really meant to provide context for actions that will have a large impact on the user.
 * For example: Deleting an item that will not be reversible.
 *
 * @see https://www.figma.com/file/LdhxwbVqKRRLpyK5ekr4Q8/Component-Library?node-id=1812%3A904
 */
export const Alert = React.forwardRef<HTMLDivElement, AlertProps>((props, ref) => {
  const { children, cta, error, hideIcon = false, plain = false, title, variant, sx } = props;
  const hasError = !!error;
  const resolvedVariant: AlertVariant = variant || (hasError ? 'error' : 'info');

  const { Icon, backgroundColor, borderColor, iconColor } = AlertPropsByVariant[resolvedVariant];

  const rootSx: SxProps<Theme> = plain
    ? {
        display: 'flex',
        gap: '12px',
        padding: '4px 0',
      }
    : {
        display: 'flex',
        gap: '12px',
        padding: '12px 16px',
        borderRadius: '4px',
        minWidth: '250px',
        backgroundColor,
        border: `1px solid ${borderColor}`,
      };

  return (
    <Box sx={mergeSxProps(rootSx, sx)} role="alert" ref={ref}>
      {!hideIcon && <Icon size={24} color={iconColor} style={{ flexShrink: 0 }} />}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 1,
            height: '100%',
          }}
        >
          {title && (
            <Text variant="heading5" bold>
              {title}
            </Text>
          )}
          {!!children && <Text variant="body2">{children}</Text>}
          {hasError && <Text variant="body2">{parseError(error)}</Text>}
        </Box>
        {isDefined(cta) && (
          <Text variant="body2" bold>
            <Link
              component="button"
              aria-disabled={cta.disabled}
              disabled={Boolean(cta.disabled)}
              onClick={cta.onClick}
              underline="none"
              color="secondary"
            >
              {cta.label}
            </Link>
          </Text>
        )}
      </Box>
    </Box>
  );
});

Alert.displayName = 'Alert';
